import React, { useState, useEffect } from "react";
import useContract from "../hooks/useContract";
import { toast } from "react-hot-toast";

const Admin = () => {
  const {
    isConnected,
    isAdmin,

    // core
    setPauseState,
    setWallets,
    setCardPrice,
    setTax,

    // referral
    setMaxReferralLevels,
    setDirectReferralPercent,
    setReferralPercents,
    setMinQualifiedCardsForReferral,

    // burn
    setBurnThreshold,
    executeFullBurn,

    // recovery
    recoverERC20,
    recoverBNB,

    // system
    manualTopUp,
    adminSettleAllUsers,
    adminSettleUserRange,

    // config
    setWithdrawLimit,
    setWithdrawCooldown,
    setCardDuration,
    setBonusDivider,
    setBasePercents,
    setRoiRatesBps,
    setRankThresholds,
    setEarningsPause,

    // dex / tokens
    setManualPrice,
    setRouter,
    setDefaultPayoutToken,
    setApprovedPayoutToken,
    setKindToken,
    setWNative,
    setPairManually,

    // ownership
    transferOwnership,
    renounceOwnership,
  } = useContract();

  const [isAdminUser, setIsAdminUser] = useState(null);
  const [f, setF] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const call = async (fn, args = []) => {
    try {
      toast.loading("Processing...", { id: "tx" });
      await fn(...args);
      toast.success("✅ Success", { id: "tx" });
    } catch (e) {
      toast.error(e.message || "Error", { id: "tx" });
    }
  };

  useEffect(() => {
    if (isConnected) {
      isAdmin().then(setIsAdminUser).catch(() => setIsAdminUser(false));
    }
  }, [isConnected]);

  if (!isConnected) return <div className="text-white p-10">Connect Wallet</div>;
  if (!isAdminUser) return <div className="text-white p-10">Not Admin</div>;

  const box = "bg-[#171717] p-8 rounded-3xl border border-[#2A2A2A] space-y-6 mb-10";
  const input = "bg-[#111] border border-[#2A2A2A] px-4 py-3 rounded-xl w-full text-white";
  const btn = "px-5 py-3 rounded-xl font-bold bg-[#D3AF37] text-black w-full mt-2";

  return (
    <div className="min-h-screen p-6 bg-[#111] max-w-6xl mx-auto text-white space-y-12">

      {/* PAUSE */}
      <div className={box}>
        <h2 className="font-bold text-white">Pause System</h2>
        <input className={input} placeholder="Earnings true/false" onChange={e=>set("earnings", e.target.value==="true")} />
        <input className={input} placeholder="Buy true/false" onChange={e=>set("buy", e.target.value==="true")} />
        <input className={input} placeholder="Withdraw true/false" onChange={e=>set("withdraw", e.target.value==="true")} />
        <button className={btn} onClick={()=>call(setPauseState,[f.earnings,f.buy,f.withdraw])}>Apply</button>
      </div>

      {/* PRICE */}
      <div className={box}>
        <h2 className="font-bold text-white">Price & Tax</h2>
        <input className={input} placeholder="Card Price" onChange={e=>set("price",e.target.value)} />
        <button className={btn} onClick={()=>call(setCardPrice,[f.price])}>Set Price</button>

        <input className={input} placeholder="Tax %" onChange={e=>set("tax",e.target.value)} />
        <button className={btn} onClick={()=>call(setTax,[f.tax])}>Set Tax</button>
      </div>

      {/* WITHDRAW */}
      <div className={box}>
        <h2 className="font-bold text-white">Withdraw Settings</h2>
        <input className={input} placeholder="Limit" onChange={e=>set("limit",e.target.value)} />
        <button className={btn} onClick={()=>call(setWithdrawLimit,[f.limit])}>Set Limit</button>

        <input className={input} placeholder="Cooldown (sec)" onChange={e=>set("cool",e.target.value)} />
        <button className={btn} onClick={()=>call(setWithdrawCooldown,[f.cool])}>Set Cooldown</button>
      </div>

      {/* CARD */}
      <div className={box}>
        <h2 className="font-bold text-white">Card Settings</h2>
        <input className={input} placeholder="Duration (sec)" onChange={e=>set("dur",e.target.value)} />
        <button className={btn} onClick={()=>call(setCardDuration,[f.dur])}>Set Duration</button>

        <input className={input} placeholder="Bonus Divider" onChange={e=>set("bonus",e.target.value)} />
        <button className={btn} onClick={()=>call(setBonusDivider,[f.bonus])}>Set Bonus</button>
      </div>

      {/* REFERRAL */}
      <div className={box}>
        <h2 className="font-bold text-white">Referral</h2>

        <input className={input} placeholder="Max Levels" onChange={e=>set("levels",e.target.value)} />
        <button className={btn} onClick={()=>call(setMaxReferralLevels,[f.levels])}>Set Levels</button>

        <input className={input} placeholder="Direct %" onChange={e=>set("direct",e.target.value)} />
        <button className={btn} onClick={()=>call(setDirectReferralPercent,[f.direct])}>Set Direct</button>

        <input className={input} placeholder="Min Qualified Cards" onChange={e=>set("minQ",e.target.value)} />
        <button className={btn} onClick={()=>call(setMinQualifiedCardsForReferral,[f.minQ])}>Set Min</button>

        <input className={input} placeholder="Referral Percents (comma)" onChange={e=>set("refp",e.target.value.split(","))} />
        <button className={btn} onClick={()=>call(setReferralPercents,[f.refp])}>Set Referral %</button>
      </div>

      {/* ROI + RANK */}
      <div className={box}>
        <h2 className="font-bold text-white">ROI & Rank</h2>

        <input className={input} placeholder="ROI BPS (comma)" onChange={e=>set("roi",e.target.value.split(","))} />
        <button className={btn} onClick={()=>call(setRoiRatesBps,[f.roi])}>Set ROI</button>

        <input className={input} placeholder="Rank Thresholds (comma)" onChange={e=>set("rank",e.target.value.split(","))} />
        <button className={btn} onClick={()=>call(setRankThresholds,[f.rank])}>Set Rank</button>
      </div>

      {/* BURN */}
      <div className={box}>
        <h2 className="font-bold text-white">Burn</h2>
        <input className={input} placeholder="Threshold USD" onChange={e=>set("burnT",e.target.value)} />
        <button className={btn} onClick={()=>call(setBurnThreshold,[f.burnT])}>Set Threshold</button>
        <button className={btn} onClick={()=>call(executeFullBurn)}>Execute Burn</button>
      </div>

      {/* SYSTEM */}
      <div className={box}>
        <h2 className="font-bold text-white">System</h2>
        <input className={input} placeholder="TopUp" onChange={e=>set("top",e.target.value)} />
        <button className={btn} onClick={()=>call(manualTopUp,[f.top])}>TopUp</button>

        <button className={btn} onClick={()=>call(adminSettleAllUsers)}>Settle All</button>

        <input className={input} placeholder="Start Index" onChange={e=>set("start",e.target.value)} />
        <input className={input} placeholder="End Index" onChange={e=>set("end",e.target.value)} />
        <button className={btn} onClick={()=>call(adminSettleUserRange,[f.start,f.end])}>Settle Range</button>
      </div>

      {/* TOKEN / ROUTER */}
      <div className={box}>
        <h2 className="font-bold text-white">DEX / Tokens</h2>

        <input className={input} placeholder="Router" onChange={e=>set("router",e.target.value)} />
        <button className={btn} onClick={()=>call(setRouter,[f.router])}>Set Router</button>

        <input className={input} placeholder="Default Token" onChange={e=>set("def",e.target.value)} />
        <button className={btn} onClick={()=>call(setDefaultPayoutToken,[f.def])}>Set Default</button>

        <input className={input} placeholder="Token" onChange={e=>set("token",e.target.value)} />
        <input className={input} placeholder="Approved true/false" onChange={e=>set("approved",e.target.value==="true")} />
        <button className={btn} onClick={()=>call(setApprovedPayoutToken,[f.token,f.approved])}>Approve Token</button>

        <input className={input} placeholder="Manual Price" onChange={e=>set("price2",e.target.value)} />
        <button className={btn} onClick={()=>call(setManualPrice,[f.token,f.price2])}>Set Price</button>

        <input className={input} placeholder="Pair" onChange={e=>set("pair",e.target.value)} />
        <button className={btn} onClick={()=>call(setPairManually,[f.token,f.pair])}>Set Pair</button>
      </div>

      {/* OWNERSHIP */}
      <div className={box}>
        <h2 className="font-bold text-white">Ownership</h2>

        <input className={input} placeholder="New Owner" onChange={e=>set("owner",e.target.value)} />
        <button className={btn} onClick={()=>call(transferOwnership,[f.owner])}>Transfer</button>

        <button className={btn} onClick={()=>call(renounceOwnership)}>Renounce</button>
      </div>

      {/* RECOVERY */}
      <div className={box}>
        <h2 className="font-bold text-white">Recovery</h2>

        <input className={input} placeholder="Token" onChange={e=>set("recToken",e.target.value)} />
        <input className={input} placeholder="Amount" onChange={e=>set("amount",e.target.value)} />

        <button className={btn} onClick={()=>call(recoverERC20,[f.recToken,f.amount])}>Recover Token</button>
        <button className={btn} onClick={()=>call(recoverBNB,[f.amount])}>Recover BNB</button>
      </div>

    </div>
  );
};

export default Admin;